import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useTable, useFlexLayout, useSortBy, useFilters, usePagination } from "react-table"
import { useSticky } from "react-table-sticky"

// UTILS
import { exportTable } from "Components/TableComponent/functions"

// COMPONENTS
import ExportMenu from "Components/ExportMenu"
import Pagination from "./Pagination"
// STYLES
import { FilterContainer, HeaderContent } from "../WidgetTable.styles"

const Table = ({
  columns,
  data,
  enableFilters,
  enableFooters,
  enablePagination,
  nbRowsPerPage,
  enableExportMenu,
  enableCountRow,
  exportMenuId,
  exportsTitle,
  exportPNG,
  widgetTraduction,
  // STYLES
  paginationColor,
  exportMenuPosition,
  pdfExportStyles,
  // STICKY COLUMN
  freezeFirstColumn,
}) => {
  const getTableTransformedData = (data, columns) => {
    if (data.length > 0) {
      var dataKeys = Object.keys(data[0])

      var missingColumns = []
      columns.forEach(column => {
        var { accessor } = column
        if (!dataKeys.includes(accessor)) missingColumns.push(accessor)
      })

      return data.map(dataRow => {
        let newDataRow = { ...dataRow }
        missingColumns.forEach(accessor => {
          newDataRow[accessor] = ""
        })
        return newDataRow
      })
    } else {
      return data
    }
  }

  const [columnToUse, setColumnToUse] = useState(columns)
  const [dataToUse, setDataToUse] = useState(getTableTransformedData(data, columns))

  useEffect(() => {
    var newDataToUse = getTableTransformedData(data, columns)
    setDataToUse(newDataToUse)
    setColumnToUse(columns)
  }, [data, columns])

  const { LINES } = widgetTraduction

  const tbodyId = `tbody${Math.random()}`
  const theadId = `thead${Math.random()}`
  const tfootId = `tfoot${Math.random()}`

  const getScrollbarWidth = () => {
    // Creating invisible container
    const outer = document.createElement("div")
    outer.style.visibility = "hidden"
    outer.style.overflow = "scroll" // forcing scrollbar to appear
    outer.style.msOverflowStyle = "scrollbar" // needed for WinJS apps
    document.body.appendChild(outer)

    // Creating inner element and placing it in the container
    const inner = document.createElement("div")
    outer.appendChild(inner)

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth

    // Removing temporary elements from the DOM
    outer.parentNode.removeChild(outer)

    return scrollbarWidth
  }

  useEffect(() => {
    const elBody = document.getElementById(tbodyId)
    const elHead = document.getElementById(theadId)
    if (enableFooters) var elFoot = document.getElementById(tfootId)

    if (elBody && elHead) {
      const scrollBarWidth = getScrollbarWidth()
      const bodyHeight = elBody.clientHeight
      const bodyScrollHeight = elBody.scrollHeight

      if (bodyScrollHeight > bodyHeight) {
        elHead.style.paddingRight = scrollBarWidth + "px"
        if (enableFooters) elFoot.style.paddingRight = scrollBarWidth + "px"
      } else {
        elHead.style.removeProperty("padding-right")
        if (enableFooters) elFoot.style.removeProperty("padding-right")
      }
    }
  })

  function DefaultColumnFilter({ column: { filterValue, preFilteredRows, setFilter } }) {
    const count = preFilteredRows.length

    return (
      <input
        value={filterValue || ""}
        onChange={e => setFilter(e.target.value || undefined)}
        placeholder={`Default placeholder...`}
      />
    )
  }

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter, //default Filter UI
    }),
    []
  )
  const useTableObj = useTable(
    {
      columns: columnToUse,
      data: dataToUse,
      defaultColumn,
      initialState: { pageSize: nbRowsPerPage },
    },
    useFilters,
    useFlexLayout,
    useSticky,
    useSortBy,
    usePagination
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    filteredFlatRows,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTableObj

  const footerGroups = headerGroups.slice().reverse()

  const getFooterValues = () => {
    const tableFooter = document.getElementById(tfootId)
    const tableFooterCells = tableFooter.getElementsByClassName("td")
    const tableFooterValues = Array.from(tableFooterCells).map(cell => cell.innerHTML)
    let values = {}
    tableFooterValues.forEach((value, index) => {
      values[columns[index].accessor] = value
    })
    const row = { values }
    return row
  }

  const exportDatas = (fileType, fileName, styles = {}) => {
    const name = fileName !== "" ? fileName : "export"

    const title = name.replace(/ /g, "_")
    const datasToExport = {
      columns: [...columns],
      data: [...filteredFlatRows],
    }
    if (enableFooters) {
      const footerRow = getFooterValues()
      datasToExport.data.push(footerRow)
    }
    exportTable({ ...datasToExport, fileType, fileName: title, styles })
  }

  if (pageSize !== nbRowsPerPage) setPageSize(nbRowsPerPage)

  return (
    <>
      <div className={freezeFirstColumn ? "table sticky" : "table"} {...getTableProps()}>
        <div id={theadId} className="thead">
          {headerGroups.map(headerGroup => (
            <div className="tr" {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <div className="th" {...column.getHeaderProps(column.getSortByToggleProps())}>
                  <HeaderContent>
                    {column.render("Header")}
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <i className="fas fa-caret-down" />
                      ) : (
                        <i className="fas fa-caret-up" />
                      )
                    ) : (
                      ""
                    )}
                  </HeaderContent>
                  {enableFilters && (
                    <FilterContainer>
                      {column.canFilter ? column.render("Filter") : null}
                    </FilterContainer>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div id={tbodyId} className="tbody" {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row)
            return (
              <div className="tr" {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <div className="td" {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
        {enableFooters && (
          <div id={tfootId} className="tfoot">
            {footerGroups.map(group => (
              <div className="tr" {...group.getHeaderGroupProps()}>
                {group.headers.map(column => (
                  <div className="td" {...column.getHeaderProps()}>
                    {column.render("Footer")}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      {enablePagination && (
        <Pagination
          pagesLength={pageOptions.length}
          pageIndex={pageIndex}
          // NAVIGATIONS
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          previousPage={previousPage}
          canNextPage={canNextPage}
          nextPage={nextPage}
          // STYLES
          paginationColor={paginationColor}
        />
      )}
      {enableExportMenu && (
        <ExportMenu
          exportMenuPosition={exportMenuPosition}
          exportMenuId={exportMenuId}
          exportCSV={() => exportDatas("csv", exportsTitle)}
          exportPNG={exportPNG}
          exportXLSX={() => exportDatas("xlsx", exportsTitle)}
          exportPDF={() => exportDatas("pdf", exportsTitle, pdfExportStyles)}
        />
      )}
      {enableCountRow && (
        <div style={{ position: "absolute", bottom: "0", right: "0" }}>
          {filteredFlatRows.length} {LINES}
        </div>
      )}
    </>
  )
}

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  enableFilters: PropTypes.bool,
  enableFooters: PropTypes.bool,
  enablePagination: PropTypes.bool,
  nbRowsPerPage: PropTypes.number,
  enableExportMenu: PropTypes.bool,
  enableCountRow: PropTypes.bool,
  exportMenuId: PropTypes.string,
  exportsTitle: PropTypes.string,
  exportPNG: PropTypes.func,
  // STYLES
  paginationColor: PropTypes.string,
  exportMenuPosition: PropTypes.object,
}

export default Table
